import { FormField, Input } from "@design";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { AiModelResponse } from "@/api";
import { withForm } from "@/components/form/form";
import { PageSection } from "@/components/section/SectionBlocks";
import { LabeledSelect } from "./LabeledSelect";
import { emptyModelValues } from "./modelForm";
import { replacementCandidates } from "./modelOptions";

function DateInput({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: string; onChange: (v: string) => void }>) {
  const id = useId();
  return (
    <FormField label={label} htmlFor={id}>
      <Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
    </FormField>
  );
}

/** Configure only: mark deprecated, with a sunset date and recommended replacement. */
export const DeprecationFields = withForm({
  defaultValues: emptyModelValues(),
  props: { model: null as AiModelResponse | null, models: [] as AiModelResponse[] },
  render: function DeprecationFieldsRender({ form, model, models }) {
    const { t } = useTranslation("adminModels");
    const candidates = model ? replacementCandidates(model, models) : [];
    // Keep a stored replacement visible even if it's no longer an active candidate.
    const stored = models.find((m) => m.id === model?.replacementModelId);
    const options = [
      { value: "", label: t("form.deprecation.noReplacement") },
      ...candidates.map((m) => ({ value: m.id, label: m.name })),
      ...(stored && !candidates.includes(stored) ? [{ value: stored.id, label: stored.name }] : []),
    ];
    return (
      <PageSection title={t("form.deprecation.title")} description={t("form.deprecation.description")}>
        <form.AppField name="isDeprecated">
          {(field) => <field.ToggleField label={t("form.deprecation.toggle")} />}
        </form.AppField>
        <form.Subscribe selector={(s) => s.values.isDeprecated}>
          {(isDeprecated) =>
            isDeprecated && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <form.Field name="sunsetDate">
                  {(field) => (
                    <DateInput
                      label={t("form.deprecation.sunsetDate")}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>
                <form.Field name="replacementModelId">
                  {(field) => (
                    <LabeledSelect
                      label={t("form.deprecation.replacement")}
                      value={field.state.value}
                      options={options}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>
              </div>
            )
          }
        </form.Subscribe>
      </PageSection>
    );
  },
});
